import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { tmdb, hls, iziplayAuth, iziplaySocial } from '../tools/api'
import { isErrorResponse } from '../tools/typeGuards'

import './admin.css'
import { displayNotification } from '../state/ui'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Select, TextArea, TextField } from '@iziplay/ui-kit'

type NotificationType = 'info' | 'success' | 'error'

type SortKey = 'id' | 'username' | 'role' | 'managerName'

export function Admin() {
  const [users, setUsers] = useState<Iziplay.User[]>([])
  const [clients, setClients] = useState<Iziplay.Client[]>([])
  const [addUserModal, setAddUserModal] = useState(false)
  const [editUserModal, setEditUserModal] = useState(false)
  const [notificationType, setNotificationType] = useState<NotificationType>('info')
  const [notificationText, setNotificationText] = useState('')
  const [editUser, setEditUser] = useState<Iziplay.User>()
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<number>(0)
  const [sortKey, setSortKey] = useState<SortKey>('username')
  const [sortAsc, setSortAsc] = useState(true)
  const [password, setPassword] = useState('')
  const [settings, setSettings] = useState('')
  const [fileId, setFileId] = useState('')

  async function getUsers() {
    try {
      const res = await iziplayAuth().get<Iziplay.UsersResponse>('/users/')
      setUsers(res.data.users)
    } catch (error) {
      if (!isErrorResponse(error) || !error.response) return
      displayNotification(error.response.data.err, 'error')
    }
  }

  async function getConnected() {
    try {
      const res = await iziplaySocial().get<Iziplay.ClientsResponse>('/wsConnected/')
      setClients(res.data.clients)
    } catch (error) {
      if (!isErrorResponse(error) || !error.response) return
      displayNotification(error.response.data.err, 'error')
    }
  }

  useEffect(() => {
    getUsers()
    getConnected()
  }, [])

  async function openEditUserModal(user: Iziplay.User) {
    setEditUserModal(true)
    setEditUser(user)
    setUsername(user.username)
    setEmail(user.mail)
    setRole(user.role)

    try {
      const res = await iziplaySocial().get<Iziplay.SettingsReponse>(`/settings/${user.id}`)
      setSettings(JSON.stringify(res.data, null, 2))
    } catch {
      // Leaves the settings box empty; the user row is still usable.
    }
  }

  async function addUser() {
    try {
      await iziplayAuth().post('/users/', {
        username,
        mail: email,
        password
      })
      displayNotification('User added', 'success')
      getUsers()
      getConnected()
    } catch (error) {
      if (!isErrorResponse(error) || !error.response) return
      displayNotification(error.response.data.err, 'error')
    }
    setAddUserModal(false)
  }

  async function updateUser() {
    try {
      await iziplayAuth().put(`/users/${editUser?.id}`, {
        username: username === editUser?.username ? '' : username,
        mail: email === editUser?.mail ? '' : email,
        ...(role !== editUser?.role ? { role } : {})
      })
      displayNotification('User updated', 'success')
      getUsers()
      getConnected()
    } catch (error) {
      if (!isErrorResponse(error) || !error.response) return
      displayNotification(error.response.data.err, 'error')
    }
    setEditUserModal(false)
  }

  function randomPass() {
    const words = ['pierre', 'papier', 'ciseau', 'soleil', 'bite', 'jambon', 'poulet']
    const chars = ['!', '?', '#', '*', '.']
    let pass = words[Math.floor(Math.random() * words.length)]
    pass += chars[Math.floor(Math.random() * chars.length)]
    pass += Math.floor(Math.random() * 90) + 10
    setPassword(pass)
    return pass
  }

  async function reinitUserPassword() {
    const pass = randomPass()

    try {
      await iziplayAuth().put(`/users/${editUser?.id}`, {
        password: pass
      })
      displayNotification('User updated', 'success')
      window.alert(`New password is: ${pass}`)
    } catch (error) {
      if (!isErrorResponse(error) || !error.response) return
      displayNotification(error.response.data.err, 'error')
    }
    setEditUserModal(false)
  }

  async function updateUserSettings() {
    let data = {}
    try {
      data = JSON.parse(settings)
      await iziplaySocial().post(`/settings/${editUser?.id}`, data)
      displayNotification('', 'success')
    } catch (error) {
      if (!isErrorResponse(error) || !error.response) return
      displayNotification(error.response.data.err, 'error')
    }
  }

  async function deleteUser() {
    if (!confirm(`Are u sure to delete '${editUser?.id}'?`)) {
      return
    }
    try {
      await iziplayAuth().delete(`/users/${editUser?.id}`)
      displayNotification('', 'success')
    } catch {
      displayNotification('', 'error')
    }

    setEditUserModal(false)
    getUsers()
  }

  // Stops the download, removes the transcoded output and resets the file, so
  // opening its page restarts it from scratch.
  async function deleteFile(id: string) {
    if (!id) {
      displayNotification('Enter a file id first', 'error')
      return
    }
    if (
      !confirm(
        `Delete '${id}'? Its download and transcoded output will be removed; it will restart from scratch the next time its page is opened.`
      )
    ) {
      return
    }

    try {
      await hls().delete(`/${id}`)
      displayNotification('File deleted', 'success')
    } catch (error) {
      if (!isErrorResponse(error) || !error.response) return
      displayNotification(error.response.data.err, 'error')
    }
  }

  async function sendNotif() {
    try {
      await iziplaySocial().post('/immediateNotification', {
        text: notificationText,
        type: notificationType
      })
    } catch (error) {
      if (!isErrorResponse(error) || !error.response) return
      displayNotification(error.response.data.err, 'error')
    }
  }

  function sortBy(key: SortKey) {
    if (key === sortKey) {
      setSortAsc(!sortAsc)
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  const sortedUsers = [...users].sort((a, b) => {
    const av = a[sortKey]
    const bv = b[sortKey]
    const cmp =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''))
    return sortAsc ? cmp : -cmp
  })

  const sortArrow = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '')

  async function openLink(client: Iziplay.Client) {
    if (client.viewing && client.viewing.imdb) {
      try {
        const res = await tmdb.get(`/find/${client.viewing.imdb}`, {
          params: {
            external_source: 'imdb_id'
          }
        })
        if (client.viewing.type === 'tvshow') {
          window.open(`https://www.themoviedb.org/tv/${res.data.tv_results[0].id}`)
        } else if (client.viewing.type === 'movie') {
          window.open(`https://www.themoviedb.org/movie/${res.data.movie_results[0].id}`)
        }
      } catch (error) {
        if (!isErrorResponse(error) || !error.response) return
        displayNotification(error.response.data.err, 'error')
      }
    }
  }

  return (
    <div className="admin scrollable">
      <Dialog
        open={addUserModal}
        onClose={() => setAddUserModal(false)}
        className="admin-modal"
      >
        <DialogTitle>Add user</DialogTitle>
        <DialogContent>
          <TextField
            type="text"
            label="Username"
            id="add-username"
            onChange={e => setUsername(e.target.value)}
          />
          <TextField
            type="email"
            label="Mail"
            id="add-mail"
            onChange={e => setEmail(e.target.value)}
          />
          <div className="password-row">
            <TextField
              type="text"
              label="Password"
              id="add-password"
              onChange={e => setPassword(e.target.value)}
              value={password}
            />
            <Button variant="outlined" onClick={() => randomPass()} title="Generate random password">
              ⚷
            </Button>
          </div>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => addUser()}>
            Add user
          </Button>
          <Button onClick={() => setAddUserModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editUserModal}
        onClose={() => setEditUserModal(false)}
        className="admin-modal"
      >
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent>
          <TextField
            type="text"
            label="Username"
            id="edit-username"
            onChange={e => setUsername(e.target.value)}
            value={username}
          />
          <TextField
            type="email"
            label="Mail"
            id="edit-mail"
            onChange={e => setEmail(e.target.value)}
            value={email}
          />
          <Select
            id="edit-role"
            label="Role"
            value={role}
            onChange={value => setRole(value)}
            options={[
              { value: 0, label: 'User' },
              { value: 1, label: 'Admin' }
            ]}
          />
          <TextArea
            label="Settings (JSON)"
            id="edit-settings"
            className="settings-json"
            onChange={e => setSettings(e.target.value)}
            value={settings}
          />
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={() => reinitUserPassword()}>
            Reinit Password
          </Button>
          <Button variant="contained" color="primary" onClick={() => updateUser()}>
            Edit user
          </Button>
          <Button variant="contained" color="primary" onClick={() => updateUserSettings()}>
            Edit settings
          </Button>
          <Button color="error" onClick={() => deleteUser()}>
            Delete user
          </Button>
          <Button onClick={() => setEditUserModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <div className="users box">
        <div className="title">
          Users{' '}
          <Button variant="outlined" size="small" onClick={() => getUsers()}>
            Reload
          </Button>
        </div>
        <table>
          <thead>
            <tr>
              <th className="sortable" onClick={() => sortBy('id')}>ID{sortArrow('id')}</th>
              <th className="sortable" onClick={() => sortBy('username')}>User{sortArrow('username')}</th>
              <th className="sortable" onClick={() => sortBy('role')}>Role{sortArrow('role')}</th>
              <th className="sortable" onClick={() => sortBy('managerName')}>Manager{sortArrow('managerName')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(user => (
              <tr key={user.id} onClick={() => openEditUserModal(user)}>
                <td>{user.id}</td>
                <td>
                  <div className="user-cell">
                    <div
                      className="user-cell-img"
                      style={{
                        backgroundImage: `url("https://www.gravatar.com/avatar/${user.mailHash}?s=80&d=identicon")`
                      }}
                    />
                    <div className="user-cell-text">
                      <div className="user-cell-name">{user.username}</div>
                      <div className="user-cell-mail">{user.mail}</div>
                    </div>
                  </div>
                </td>
                <td>{user.role}</td>
                <td>{user.managerName}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button variant="contained" color="primary" onClick={() => setAddUserModal(true)}>
          Add user
        </Button>
      </div>

      <div className="users box">
        <div className="title">
          Connected{' '}
          <Button variant="outlined" size="small" onClick={() => getConnected()}>
            Reload
          </Button>
        </div>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Device</th>
              <th>Type</th>
              <th>IMDB</th>
              <th>Season</th>
              <th>Episode</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client, i) => (
              <tr key={`connected_${i}_${client.id}`}>
                <td>{client.id}</td>
                <td>
                  <div className="user-cell">
                    <div
                      className="user-cell-img"
                      style={{
                        backgroundImage: `url("https://www.gravatar.com/avatar/${client.mailHash}?s=80&d=identicon")`
                      }}
                    />
                    <div className="user-cell-text">
                      <div className="user-cell-name">{client.username}</div>
                      <div className="user-cell-mail">{client.mail}</div>
                    </div>
                  </div>
                </td>
                <td>{client.viewing && client.viewing.device}</td>
                <td>{client.viewing && client.viewing.type}</td>
                <td className="imdbLink" onClick={() => openLink(client)}>
                  {client.viewing && client.viewing.imdb}
                </td>
                <td>{client.viewing && client.viewing.season}</td>
                <td>{client.viewing && client.viewing.episode}</td>
                <td>
                  {client.viewing &&
                    `${Math.floor(client.viewing.time / 3600)}:${Math.floor(
                      (client.viewing.time / 60) % 60
                    )}:${Math.floor(client.viewing.time % 60)}`}
                </td>
                <td>
                  {client.viewing && (
                    <div className="file-actions">
                      <NavLink to={`/hls/${client.viewing.file}`}>View file</NavLink>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => deleteFile(client.viewing!.file)}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="box admin_files">
        <div className="title">Files</div>
        <p className="desc">
          Delete a file to make it startable again: it stops the download, removes the
          transcoded output and resets the file, which then restarts from scratch the
          next time its page is opened.
        </p>
        <div className="file-form">
          <TextField
            type="text"
            label="File id"
            id="file-id"
            onChange={e => setFileId(e.target.value)}
            value={fileId}
          />
          <Button variant="contained" color="primary" onClick={() => deleteFile(fileId)}>
            Delete file
          </Button>
        </div>
      </div>

      <div className="box admin_notif">
        <div className="title">Push notification</div>
        <p className="desc">
          Send notification to all connected users with WS (including chromecasts)
        </p>
        <div className="notif-form">
          <TextArea
            label="Text of notification"
            id="notif-text"
            onChange={e => setNotificationText(e.target.value)}
          />
          <Select
            id="notif-type"
            label="Type"
            value={notificationType}
            onChange={value => setNotificationType(value as NotificationType)}
            options={[
              { value: 'info', label: 'Info' },
              { value: 'success', label: 'Success' },
              { value: 'error', label: 'Error' }
            ]}
          />
          <div>
            <Button variant="contained" color="primary" onClick={() => sendNotif()}>
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
